/**
 * HOME PAGE - Precisely matches mockup screenshot
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { roadsAPI, venuesAPI, authAPI } from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import QRScanner from '../components/QRScanner';
import logo from '../assets/logo.png';

// Bottom navigation bar - matches mockup screenshot exactly
export function BottomNav({ active }) {
    const items = [
        { to: '/home', label: 'Home', emoji: '🏠' },
        { to: '/trail', label: 'Road', emoji: '🗺️' },
        { to: '/checkin', label: 'Check In', emoji: '📷' },
        { to: '/badges', label: 'Badges', emoji: '🏅' },
        { to: '/partners', label: 'Partners', emoji: '🤝' },
    ];

    return (
        <nav style={navStyles.nav}>
            {items.map(item => {
                const isActive = active === item.to;
                return (
                    <Link key={item.to} to={item.to} style={navStyles.link}>
                        <span style={{
                            fontSize: 26,
                            lineHeight: 1,
                            filter: isActive ? 'none' : 'grayscale(100%) opacity(45%)',
                            transition: 'filter 0.2s',
                            display: 'block'
                        }}>
                            {item.emoji}
                        </span>
                        <span style={{
                            fontSize: 10,
                            fontWeight: isActive ? '700' : '500',
                            color: isActive ? '#b8753d' : '#aaa',
                            letterSpacing: 0.1,
                            marginTop: 2
                        }}>
                            {item.label}
                        </span>
                    </Link>
                );
            })}
        </nav>
    );
}

const navStyles = {
    nav: {
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#fff',
        display: 'flex', justifyContent: 'space-around', alignItems: 'center',
        padding: '10px 0 22px',
        borderTop: '1px solid #f0f0f0',
        boxShadow: '0 -2px 16px rgba(0,0,0,0.07)',
        zIndex: 4000
    },
    link: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textDecoration: 'none', gap: 3, minWidth: 52, paddingTop: 2
    },
};

export default function Home() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [roads, setRoads] = useState([]);
    const [selectedRoad, setSelectedRoad] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recentStops, setRecentStops] = useState([]);
    const [showRoadDropdown, setShowRoadDropdown] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [checkinResult, setCheckinResult] = useState(null);

    useEffect(() => {
        const loadInitial = async () => {
            try {
                const [roadsRes, meRes, checkinsRes] = await Promise.all([
                    roadsAPI.getAll(),
                    authAPI.getMe(),
                    venuesAPI.getUserCheckins()
                ]);
                setRoads(roadsRes.data);
                const savedSlug = localStorage.getItem('selectedRoadSlug');
                const roadToSelect = savedSlug 
                    ? roadsRes.data.find(r => r.slug === savedSlug) || roadsRes.data[0]
                    : roadsRes.data[0];
                
                if (roadToSelect) {
                    setSelectedRoad(roadToSelect);
                }
                setProgress(meRes.data.checkin_summary);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadInitial();
    }, []);

    useEffect(() => {
        if (!selectedRoad) return;
        
        const loadVenues = async () => {
            try {
                const [venuesRes, checkinsRes] = await Promise.all([
                    venuesAPI.getAll(selectedRoad.slug),
                    venuesAPI.getUserCheckins()
                ]);
                
                const allVenues = venuesRes.data.map(v => ({
                    ...v,
                    checked_in: checkinsRes.data.some(c => c.venue_id === v.id)
                }));
                
                // Show the first 4 as "Recent/Featured" stops for this road
                setRecentStops(allVenues.slice(0, 4));
            } catch (err) {
                console.error(err);
            }
        };
        loadVenues();
    }, [selectedRoad]);

    const handleScanSuccess = async (token) => {
        setShowScanner(false);
        setLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const res = await venuesAPI.submitCheckin(token, position.coords.latitude, position.coords.longitude);
                    setCheckinResult({ success: true, message: res.data.message });
                    // Refresh data
                    const [meRes, checkinsRes] = await Promise.all([authAPI.getMe(), venuesAPI.getUserCheckins()]);
                    setProgress(meRes.data.checkin_summary);
                } catch (err) {
                    setCheckinResult({ success: false, message: err.response?.data?.message || 'Check-in failed' });
                } finally {
                    setLoading(false);
                }
            },
            () => {
                setCheckinResult({ success: false, message: 'Location access required for check-in.' });
                setLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const roadProgress = progress?.find(p => p.road_slug === selectedRoad?.slug);
    const totalStops = roadProgress?.total_stops || selectedRoad?.total_stops || 0;
    const checkedCount = roadProgress?.checked_in || 0;
    const percent = totalStops > 0 ? Math.round((checkedCount / totalStops) * 100) : 0;
    const isComplete = checkedCount >= totalStops && totalStops > 0;
    const showTshirtBanner = isComplete && !user?.tshirt_size;

    if (loading) return <LoadingSpinner />;

    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

    return (
        <div style={s.screen}>
            {/* Dark Header */}
            <div style={s.header}>
                <div style={s.headerInner}>
                    <div>
                        <img src={logo} alt="The Copper Road" style={s.brandLogo} />
                        <h1 style={s.h1}>{greeting},<br />{user?.first_name || 'Matt'} 👋</h1>
                        <p style={s.subtext}>You're {checkedCount} of {totalStops} stops in — keep riding!</p>
                    </div>
                    {/* Header Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12 }}>
                        {/* Active Road Pill with Dropdown */}
                        <div style={{ position: 'relative' }}>
                            <div 
                                style={{ ...s.roadPill, cursor: 'pointer' }}
                                onClick={() => setShowRoadDropdown(!showRoadDropdown)}
                            >
                                <span style={s.pillDiamond}>🔸</span>
                                <div>
                                    <div style={s.pillTop}>ACTIVE ROAD</div>
                                    <div style={s.pillBottom}>{selectedRoad?.region || 'Kentucky'}</div>
                                </div>
                                <span style={{ ...s.pillArrow, transform: showRoadDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                            </div>

                            {/* Dropdown Menu */}
                            {showRoadDropdown && (
                                <div style={s.dropdownMenu}>
                                    <div style={s.dropdownHeader}>Select Region</div>
                                    {roads.map(road => (
                                        <div 
                                            key={road.id} 
                                            style={road.id === selectedRoad?.id ? s.dropdownItemActive : s.dropdownItem}
                                            onClick={() => {
                                                setSelectedRoad(road);
                                                localStorage.setItem('selectedRoadSlug', road.slug);
                                                setShowRoadDropdown(false);
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span>{road.name}</span>
                                                {road.id === selectedRoad?.id && <span>✓</span>}
                                            </div>
                                            <div style={s.dropdownItemSub}>{road.region}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Profile Link */}
                        <div
                            onClick={() => window.location.href = '/profile'}
                            style={{
                                width: 44, height: 44, borderRadius: '50%',
                                background: '#ffffff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                                cursor: 'pointer', border: '2px solid #c48f4a',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                transition: 'transform 0.2s',
                            }}
                        >
                            <span style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))' }}>👤</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Road Selector Tabs (if multiple) */}
            {roads.length > 1 && (
                <div style={s.tabsWrap}>
                    {roads.map(road => (
                        <button
                            key={road.id}
                            style={road.id === selectedRoad?.id ? s.tabActive : s.tabInactive}
                            onClick={() => setSelectedRoad(road)}
                        >
                            {road.name}
                        </button>
                    ))}
                </div>
            )}

            {/* T-Shirt Banner */}
            {showTshirtBanner && (
                <div style={s.tshirtBanner}>
                    <div style={s.tshirtIcon}>👕</div>
                    <div>
                        <div style={s.tshirtTitle}>You did it!</div>
                        <div style={s.tshirtSub}>Road complete. Check your profile for your T-Shirt.</div>
                    </div>
                </div>
            )}

            {/* Active Road Card - Striped Brown */}
            <div style={s.activeRoadCard}>
                <div style={s.arcTag}>
                    <span style={s.arcDiamond}>🔸</span>
                    <span style={s.arcTagText}>ACTIVE ROAD</span>
                </div>
                <div style={s.arcTitle}>{selectedRoad?.name || 'The Copper Road'}</div>
                <div style={s.arcSub}>{selectedRoad?.tagline || selectedRoad?.region || 'Kentucky'}</div>

                {/* Progress Bar added as requested */}
                <div style={s.progressWrap}>
                    <div style={s.progressRow}>
                        <span style={s.progressText}>{checkedCount} of {totalStops} stops</span>
                        <span style={s.progressPercent}>{percent}%</span>
                    </div>
                    <div style={s.progressBarTrack}>
                        <div style={{ ...s.progressBarFill, width: `${Math.min(percent, 100)}%` }}></div>
                    </div>
                </div>
            </div>

            {/* Scan CTA */}
            <div style={s.scanWrap}>
                <button style={s.scanBtn} onClick={() => navigate('/checkin')}>
                    📷 Scan a Venue Check-In
                </button>
            </div>

            {/* Quick Actions */}
            <div style={s.quickActionsRow}>
                <button style={s.actionBtn} onClick={() => navigate('/trail')}>
                    📍 View All Stops
                </button>
                <button style={s.actionBtn} onClick={() => navigate('/trail?map=true')}>
                    🗺️ Map View
                </button>
            </div>

            {/* Recent Stops */}
            <div style={s.sectionRow}>
                <span style={s.sectionTitle}>Recent Stops</span>
                <Link to="/trail" style={s.allLink}>All Stops →</Link>
            </div>

            <div style={s.stopsList}>
                {recentStops.map(stop => (
                    <div key={stop.id} style={s.stopCard} onClick={() => navigate('/trail')}>
                        <div style={s.stopIconWrap}>
                            <span style={s.stopIconEmoji}>{stop.icon}</span>
                        </div>
                        <div style={s.stopInfo}>
                            <div style={s.stopRoadName}>{selectedRoad?.name || 'The Copper Road'}</div>
                            <div style={s.stopName}>{stop.name}</div>
                            <div style={s.stopTagline}>{stop.tagline}</div>
                        </div>
                        {stop.checked_in ? (
                            <div style={s.checkCircleActive}>✓</div>
                        ) : (
                            <div style={s.checkCircleInactive}>0</div>
                        )}
                    </div>
                ))}
            </div>

            {/* Progress Summary for All Roads */}
            <div style={s.sectionRow}>
                <span style={s.sectionTitle}>All Roads Progress</span>
            </div>
            <div style={{ ...s.stopsList, paddingBottom: 100 }}>
                {roads.map(r => {
                    const rProg = progress?.find(p => p.road_slug === r.slug);
                    const rTotal = rProg?.total_stops || r.total_stops || 28;
                    const rChecked = rProg?.checked_in || 0;
                    return (
                        <div key={r.id} style={s.summaryCard}>
                            <div style={s.summaryName}>{r.name}</div>
                            <div style={s.summaryStats}>{rChecked} / {rTotal} Stops</div>
                        </div>
                    );
                })}
            </div>

            {showScanner && <QRScanner onScanSuccess={handleScanSuccess} onClose={() => setShowScanner(false)} />}

            {checkinResult && (
                <div style={{ ...s.toast, background: checkinResult.success ? '#2e7d32' : '#c62828' }}>
                    {checkinResult.message}
                    <button style={s.toastClose} onClick={() => setCheckinResult(null)}>✕</button>
                </div>
            )}

            <BottomNav active="/" />
        </div>
    );
}

const s = {
    toast: { position: 'fixed', bottom: 100, left: 20, right: 20, padding: 16, borderRadius: 12, color: '#fff', zIndex: 1000, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    toastClose: { background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16 },
    screen: { background: '#f5f0e6', minHeight: '100vh', paddingBottom: 0 },
    loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 20, color: '#b8753d', background: '#1c2532' },

    header: {
        background: 'linear-gradient(180deg, #161e29 0%, #1c2532 100%)',
        padding: '56px 20px 28px',
        borderBottomLeftRadius: 24,
        borderBottomRightRadius: 24,
        marginBottom: 16
    },
    headerInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    brandLogo: { height: '24px', width: 'auto', marginBottom: 8 },
    h1: { fontSize: 32, fontFamily: 'Georgia, serif', color: '#fff', margin: '0', fontWeight: 'bold', lineHeight: 1.2 },
    subtext: { fontSize: 14, color: '#687887', marginTop: 12 },

    roadPill: {
        background: 'rgba(255,255,255,0.08)', borderRadius: 24, padding: '8px 14px',
        display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
        border: '1px solid rgba(255,255,255,0.05)'
    },
    pillDiamond: { fontSize: 14, color: '#f59e0b' },
    pillTop: { fontSize: 9, color: '#c48f4a', fontWeight: '800', letterSpacing: 1 },
    pillBottom: { fontSize: 13, color: '#fff', fontWeight: '600', marginTop: 2 },
    pillArrow: { color: '#8899aa', fontSize: 10, marginLeft: 4 },

    dropdownMenu: {
        position: 'absolute', top: '110%', right: 0, width: 220,
        background: '#fff', borderRadius: 12, padding: 8,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)', zIndex: 200,
        border: '1px solid #e0d8c8'
    },
    dropdownHeader: { fontSize: 10, fontWeight: '800', color: '#855633', padding: '8px 12px', letterSpacing: 1, textTransform: 'uppercase' },
    dropdownItem: {
        padding: '12px', borderRadius: 8, cursor: 'pointer', transition: 'background 0.2s',
        color: '#1a2234', fontWeight: '600', fontSize: 14
    },
    dropdownItemActive: {
        padding: '12px', borderRadius: 8, cursor: 'pointer',
        background: '#f5ede4', color: '#855633', fontWeight: '700', fontSize: 14
    },
    dropdownItemSub: { fontSize: 11, color: '#8899aa', marginTop: 2, fontWeight: '500' },

    tabsWrap: {
        display: 'flex', overflowX: 'auto', gap: 12, padding: '0 20px 16px',
        scrollbarWidth: 'none', msOverflowStyle: 'none'
    },
    tabActive: {
        background: '#855633', color: '#fff', border: 'none', borderRadius: 20,
        padding: '8px 16px', fontSize: 14, fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'pointer'
    },
    tabInactive: {
        background: '#e0d8c8', color: '#555', border: 'none', borderRadius: 20,
        padding: '8px 16px', fontSize: 14, fontWeight: '600', whiteSpace: 'nowrap', cursor: 'pointer'
    },

    tshirtBanner: {
        margin: '0 20px 16px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        borderRadius: 16, padding: '16px', display: 'flex', alignItems: 'center', gap: 16,
        boxShadow: '0 4px 12px rgba(245,158,11,0.2)', color: '#fff'
    },
    tshirtIcon: { fontSize: 32 },
    tshirtTitle: { fontSize: 16, fontWeight: 'bold' },
    tshirtSub: { fontSize: 12, marginTop: 4, opacity: 0.9 },

    activeRoadCard: {
        margin: '0 20px 16px',
        background: `repeating-linear-gradient(
            -45deg,
            rgba(255,255,255,0.02),
            rgba(255,255,255,0.02) 10px,
            transparent 10px,
            transparent 20px
        ), linear-gradient(135deg, #7c4c2a 0%, #5c351b 100%)`,
        borderRadius: 20,
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(124, 76, 42, 0.2)'
    },
    arcTag: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 },
    arcDiamond: { fontSize: 12, color: '#f59e0b' },
    arcTagText: { fontSize: 10, color: '#a8afb5', fontWeight: '700', letterSpacing: 1.5 },
    arcTitle: { fontSize: 26, color: '#fff', fontFamily: 'Georgia, serif', fontWeight: 'bold' },
    arcSub: { fontSize: 13, color: '#a8afb5', marginTop: 6 },

    // Progress Bar styles
    progressWrap: { marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)' },
    progressRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 8 },
    progressText: { color: '#d0d4d9', fontSize: 12, fontWeight: '600' },
    progressPercent: { color: '#f59e0b', fontSize: 12, fontWeight: 'bold' },
    progressBarTrack: { height: 6, background: 'rgba(0,0,0,0.2)', borderRadius: 3, overflow: 'hidden' },
    progressBarFill: { height: '100%', background: '#f59e0b', borderRadius: 3, transition: 'width 0.4s ease' },

    scanWrap: { padding: '0 20px', marginBottom: 20 },
    scanBtn: {
        width: '100%', background: 'linear-gradient(135deg, #c07840 0%, #a15f2c 100%)',
        color: '#fff', border: 'none', borderRadius: 24, padding: '18px',
        fontSize: 16, fontWeight: 'bold', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        boxShadow: '0 6px 20px rgba(184,117,61,0.3)'
    },

    // Quick Actions
    quickActionsRow: {
        display: 'flex', gap: 12, padding: '0 20px', marginBottom: 20
    },
    actionBtn: {
        flex: 1, background: '#fff', border: '1px solid #e0d8c8', borderRadius: 16,
        padding: '12px', fontSize: 13, fontWeight: 'bold', color: '#4a4a4a',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
    },

    sectionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 24px 16px' },
    sectionTitle: { fontSize: 20, color: '#101010', fontFamily: 'Georgia, serif', fontWeight: 'bold' },
    allLink: { fontSize: 13, color: '#b8753d', textDecoration: 'none', fontWeight: '600' },

    stopsList: { padding: '0 20px' },
    stopCard: {
        background: '#fff', borderRadius: 20, padding: '16px',
        display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12,
        border: '1px solid #e6dfd3',
        boxShadow: '0 2px 8px rgba(0,0,0,0.02)', cursor: 'pointer'
    },
    stopIconWrap: {
        width: 48, height: 48, background: '#eaf4eb', borderRadius: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0
    },
    stopIconEmoji: { fontSize: 24 },
    stopInfo: { flex: 1 },
    stopRoadName: { fontSize: 10, color: '#b8753d', fontWeight: '800', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 },
    stopName: { fontSize: 15, fontWeight: 'bold', color: '#1a2234' },
    stopTagline: { fontSize: 13, color: '#8899aa', marginTop: 4 },
    checkCircleActive: {
        width: 28, height: 28, background: '#eaf4eb',
        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#34d35c', fontSize: 14, fontWeight: 'bold'
    },
    checkCircleInactive: {
        width: 28, height: 28, background: '#e1dcd1',
        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#9e978d', fontSize: 12, fontWeight: 'bold'
    },

    // Progress Summary List
    summaryCard: {
        background: '#fff', borderRadius: 16, padding: '18px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12, border: '1px solid #e6dfd3',
        boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
    },
    summaryName: { fontSize: 15, fontWeight: 'bold', color: '#1a2234' },
    summaryStats: { fontSize: 13, color: '#b8753d', fontWeight: 'bold', background: '#f5ede4', padding: '6px 12px', borderRadius: 16 }
};
